/**
 * New Sales by Salesperson — project-level rollup.
 *
 * OWNER-ONLY (Tom Clancy, id 3). Server enforces with 404. This page also
 * guards in-component so non-owners see a clean access-denied instead of
 * a broken empty state.
 *
 * For each salesperson, lists every NEW SALE in the window grouped at the
 * PROJECT level. One ACS project commonly contains multiple Sage jobs
 * (AV/IT, Lighting, Shades, Lighting Fixtures, etc.) — this view collapses
 * those into a single project row showing job count, sale value, and close
 * value. Expand a project row to see the constituent jobs.
 *
 * Built 2026-06-17.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Chip,
  Button,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtMoney = (val, opts = {}) => {
  if (val == null || val === '') return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return '—';
  const cents = opts.cents ?? false;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
};

const fmtPct = (v) => {
  if (v == null || isNaN(v)) return '—';
  const pct = v * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toISOString().slice(0, 10); } catch (_) { return String(v); }
};

const fyStartIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  return m >= 9 ? `${y}-10-01` : `${y - 1}-10-01`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

// Delta cell color: positive = green (we grew), negative = red (we shrank).
function deltaColor(v) {
  if (v == null || v === 0) return 'text.secondary';
  return v > 0 ? 'success.main' : 'error.main';
}

function DeltaIcon({ value, size = 14 }) {
  if (value == null || value === 0) return <TrendingFlatIcon sx={{ fontSize: size, color: 'text.disabled' }} />;
  return value > 0
    ? <TrendingUpIcon sx={{ fontSize: size, color: 'success.main' }} />
    : <TrendingDownIcon sx={{ fontSize: size, color: 'error.main' }} />;
}

// ─── Summary card (mirrors AR Report styling) ────────────────────────────────
function SummaryCard({ title, value, subtitle, color }) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.25, color: color || 'text.primary' }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Project row (expandable) ────────────────────────────────────────────────
function ProjectRow({ p }) {
  const [open, setOpen] = useState(false);
  const statusChip = (() => {
    if (p.status === 'Closed') return <Chip label="Closed" size="small" color="success" variant="filled" sx={{ fontWeight: 600 }} />;
    if (p.status === 'Partial') return <Chip label="Partial" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />;
    return <Chip label="Active" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
  })();

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 36, p: 0.5 }}>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <ArrowDownIcon fontSize="small" /> : <ArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{p.project_name || p.customer_name || '(unnamed)'}</Typography>
            {p.grouping === 'ar_customer' && (
              <Tooltip title="Grouped by Sage AR customer code — no D-Tools site name available. May lump multiple sites together for GC/builder customers.">
                <Chip label="AR" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }} />
              </Tooltip>
            )}
            {p.grouping === 'customer_name' && (
              <Tooltip title="Grouped by customer name — fallback when no AR code is available">
                <Chip label="name" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {p.customer_name}
            {p.city ? ` · ${p.city}${p.state ? ', ' + p.state : ''}` : ''}
            {p.ar_customer ? ` · ${p.ar_customer}` : ''}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{p.salesman}</Typography>
          {p.mixed_salesmen && (
            <Tooltip title={`Jobs sold by: ${p.mixed_salesmen_list.join(', ')}`}>
              <Chip label="mixed" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.6rem', mt: 0.25 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight={600}>{p.job_count}</Typography>
          {p.disciplines?.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
              {p.disciplines.join(', ')}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtMoney(p.value_at_sale)}</Typography>
          <Typography variant="caption" color="text.secondary">{fmtDate(p.earliest_sale_date)}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={600}>{fmtMoney(p.value_at_close)}</Typography>
          {p.status === 'Closed' ? (
            <Typography variant="caption" color="text.secondary">closed {fmtDate(p.latest_close_date)}</Typography>
          ) : (
            <Typography variant="caption" color="warning.main">in progress</Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <DeltaIcon value={p.delta} />
            <Typography variant="body2" fontWeight={600} sx={{ color: deltaColor(p.delta) }}>
              {fmtMoney(p.delta)}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: deltaColor(p.delta), display: 'block' }}>
            {fmtPct(p.delta_pct)}
          </Typography>
        </TableCell>
        <TableCell align="center">{statusChip}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderBottom: open ? '1px solid #e0e0e0' : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 1.5, bgcolor: '#fafafa' }}>
              <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5, color: '#0D2C54' }}>
                Constituent Sage Jobs ({p.jobs.length})
              </Typography>
              <Table size="small" sx={{ '& td, & th': { py: 0.25, px: 0.75, fontSize: '0.72rem' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.68rem', bgcolor: '#f0f0f0' } }}>
                    <TableCell>Job #</TableCell>
                    <TableCell>D-Tools Project</TableCell>
                    <TableCell>Sage Description</TableCell>
                    <TableCell>Discipline</TableCell>
                    <TableCell>Salesman</TableCell>
                    <TableCell>PM</TableCell>
                    <TableCell>Sale Date</TableCell>
                    <TableCell align="right">Sale Value</TableCell>
                    <TableCell align="right">Close Value</TableCell>
                    <TableCell align="right">Δ</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.jobs.map(j => {
                    const d = (j.revised_contract || 0) - (j.original_contract || 0);
                    return (
                      <TableRow key={j.job_number} hover>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{j.job_number}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontSize: '0.7rem', color: j.deal_project_name ? 'text.primary' : 'text.disabled' }}>{j.deal_project_name || '(no D-Tools deal)'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{j.job_description || '—'}</Typography></TableCell>
                        <TableCell>{j.discipline || '—'}</TableCell>
                        <TableCell>{j.salesman}</TableCell>
                        <TableCell>{j.project_manager || '—'}</TableCell>
                        <TableCell>{fmtDate(j.sale_date)}</TableCell>
                        <TableCell align="right">{fmtMoney(j.original_contract)}</TableCell>
                        <TableCell align="right">{fmtMoney(j.revised_contract)}</TableCell>
                        <TableCell align="right" sx={{ color: deltaColor(d) }}>{fmtMoney(d)}</TableCell>
                        <TableCell align="center">
                          {j.is_closed
                            ? <Chip label="Closed" size="small" color="success" sx={{ height: 18, fontSize: '0.6rem' }} />
                            : <Chip label={j.status || 'Open'} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {/* Grouping provenance footnote — lets Tom verify how the
                  jobs collapsed into one project at a glance. */}
              {p.grouping && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Grouped by: {p.grouping === 'dtools_site' ? 'D-Tools site name'
                    : p.grouping === 'local_project' ? 'local project record'
                    : p.grouping === 'ar_customer' ? 'Sage AR customer code (⚠ may lump multiple sites)'
                    : p.grouping === 'customer_name' ? 'customer name (fallback)'
                    : 'single job'}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─── Salesperson summary table ───────────────────────────────────────────────
// Standing rule: all table columns must be sortable. Defaults to highest sale
// value (Tom's most useful first read).
function SalespeopleTable({ rows, scope }) {
  const [orderBy, setOrderBy] = useState(scope === 'closed' ? 'closed_value_at_sale' : 'value_at_sale');
  const [order, setOrder] = useState('desc');

  const headers = scope === 'closed'
    ? [
        { id: 'salesman', label: 'Salesperson', align: 'left' },
        { id: 'closed_project_count', label: 'Closed Projects', align: 'center' },
        { id: 'closed_value_at_sale', label: 'Sold $', align: 'right' },
        { id: 'closed_value_at_close', label: 'Closed $', align: 'right' },
        { id: 'closed_delta', label: 'Δ $', align: 'right' },
        { id: 'closed_delta_pct', label: 'Δ %', align: 'right' },
      ]
    : [
        { id: 'salesman', label: 'Salesperson', align: 'left' },
        { id: 'project_count', label: 'Projects', align: 'center' },
        { id: 'job_count', label: 'Jobs', align: 'center' },
        { id: 'value_at_sale', label: 'Sold $', align: 'right' },
        { id: 'value_at_close', label: 'Current/Close $', align: 'right' },
        { id: 'delta', label: 'Δ $', align: 'right' },
        { id: 'delta_pct', label: 'Δ %', align: 'right' },
      ];

  const sorted = useMemo(() => {
    const r = [...rows];
    r.sort((a, b) => {
      const av = a[orderBy];
      const bv = b[orderBy];
      if (typeof av === 'string' || typeof bv === 'string') {
        return order === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      }
      const an = Number(av || 0); const bn = Number(bv || 0);
      return order === 'asc' ? an - bn : bn - an;
    });
    return r;
  }, [rows, orderBy, order]);

  const handleSort = (id) => {
    if (orderBy === id) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(id); setOrder(id === 'salesman' ? 'asc' : 'desc'); }
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5f5f5' } }}>
            {headers.map(h => (
              <TableCell key={h.id} align={h.align} sortDirection={orderBy === h.id ? order : false}>
                <TableSortLabel
                  active={orderBy === h.id}
                  direction={orderBy === h.id ? order : 'asc'}
                  onClick={() => handleSort(h.id)}
                >
                  {h.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map(s => {
            const isClosedScope = scope === 'closed';
            const sold  = isClosedScope ? s.closed_value_at_sale  : s.value_at_sale;
            const close = isClosedScope ? s.closed_value_at_close : s.value_at_close;
            const delta = isClosedScope ? s.closed_delta          : s.delta;
            const dpct  = isClosedScope ? s.closed_delta_pct      : s.delta_pct;
            const pc    = isClosedScope ? s.closed_project_count  : s.project_count;
            return (
              <TableRow key={s.salesman} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{s.salesman}</Typography></TableCell>
                <TableCell align="center">{pc}</TableCell>
                {!isClosedScope && <TableCell align="center">{s.job_count}</TableCell>}
                <TableCell align="right">{fmtMoney(sold)}</TableCell>
                <TableCell align="right">{fmtMoney(close)}</TableCell>
                <TableCell align="right" sx={{ color: deltaColor(delta), fontWeight: 600 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <DeltaIcon value={delta} />
                    {fmtMoney(delta)}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: deltaColor(delta), fontWeight: 600 }}>{fmtPct(dpct)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
// Access allowlist — mirrors App.js SALES_REPORT_ALLOWED_IDS and the
// backend ALLOWED_USER_IDS in server/routes/sales-by-salesperson.js.
// Tom (3) + Robert Kaufman (1) + Jonathan Flamm (2) + Chris Georghiou (4)
// per Tom 2026-06-17. The variable name remains 'isOwner' inside the
// component for diff clarity — it now means 'is in the allowlist'.
const ALLOWED_USER_IDS = new Set([3, 1, 2, 4]);

// ----- period helpers -----
function currentFYLabel() {
  const d = new Date();
  return (d.getMonth() >= 9 ? d.getFullYear() + 1 : d.getFullYear()) % 100;
}
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${names[m - 1]} ${y}`;
}
// Months in an ACS FY (Oct-N-1 → Sep-N), most recent month first.
function monthsInFY(fy) {
  const endYear = 2000 + Number(fy);
  const startYear = endYear - 1;
  const out = [];
  for (let m = 9; m < 12; m++) out.push(`${startYear}-${String(m + 1).padStart(2, '0')}`);
  for (let m = 0; m < 9; m++) out.push(`${endYear}-${String(m + 1).padStart(2, '0')}`);
  return out.reverse();
}

const NewSalesBySalesperson = () => {
  const { user } = useAuth();
  const isOwner = ALLOWED_USER_IDS.has(user?.id);

  // Top-level view tab: 'window' (current period detail) or 'history' (lifetime by salesperson).
  const [viewMode, setViewMode] = useState('window');

  // Period selection. periodMode controls which input is active.
  const [periodMode, setPeriodMode] = useState('fy'); // 'fy' | 'month' | 'custom'
  const [periodFY, setPeriodFY] = useState(currentFYLabel());
  const [periodMonth, setPeriodMonth] = useState(currentMonth());
  const [fromDate, setFromDate] = useState(fyStartIso());
  const [toDate, setToDate] = useState(todayIso());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [scope, setScope] = useState('all'); // 'all' | 'closed'
  const [salesmanFilter, setSalesmanFilter] = useState('all');

  // Lifetime/history data (lazy-loaded on first History tab open).
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // Build the query string for the current period selection.
  const buildQuery = useCallback(() => {
    if (periodMode === 'fy') return `fy=${periodFY}`;
    if (periodMode === 'month') return `month=${periodMonth}`;
    return `from=${fromDate}&to=${toDate}`;
  }, [periodMode, periodFY, periodMonth, fromDate, toDate]);

  // Human-readable label for the current period.
  const periodLabel = useMemo(() => {
    if (periodMode === 'fy') {
      const fy = Number(periodFY);
      const cur = currentFYLabel();
      return fy === cur ? `FY${String(fy).padStart(2, '0')} (to date)` : `FY${String(fy).padStart(2, '0')}`;
    }
    if (periodMode === 'month') return monthLabel(periodMonth);
    return `${fromDate} → ${toDate}`;
  }, [periodMode, periodFY, periodMonth, fromDate, toDate]);

  const load = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/sales-by-salesperson?${buildQuery()}`);
      setData(resp.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Failed to load report');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, isOwner]);

  const loadHistory = useCallback(async () => {
    if (!isOwner) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const resp = await api.get('/sales-by-salesperson/history');
      setHistory(resp.data);
    } catch (e) {
      setHistoryError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Failed to load history');
      setHistory(null);
    } finally {
      setHistoryLoading(false);
    }
  }, [isOwner]);

  // First mount — load the default window view.
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Lazy-load history the first time the History tab is opened.
  useEffect(() => {
    if (viewMode === 'history' && !history && !historyLoading && !historyError) {
      loadHistory();
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [viewMode]);

  // Auto re-run when the period selection changes (no manual Run needed for
  // FY/Month switches). Custom date pickers wait for the explicit Run button.
  useEffect(() => {
    if (viewMode === 'window' && periodMode !== 'custom') load();
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [periodMode, periodFY, periodMonth]);

  const projects = useMemo(() => {
    if (!data?.projects) return [];
    if (salesmanFilter === 'all') return data.projects;
    return data.projects.filter(p => p.salesman === salesmanFilter);
  }, [data, salesmanFilter]);

  // Lookup helper for the FY × salesperson matrix.
  const fyLookup = useMemo(() => {
    if (!history) return null;
    const idx = new Map();
    for (const r of (history.by_salesman_fy || [])) {
      idx.set(`${r.salesman}__${r.fy}`, r);
    }
    return idx;
  }, [history]);

  if (!isOwner) {
    return (
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error">This report is restricted.</Alert>
      </Box>
    );
  }

  const totals = data?.totals;
  const salespeople = data?.salespeople || [];

  // FYs available in dropdowns. Falls back to a reasonable default range
  // before history has loaded.
  const fyOptions = (history?.fy_list && history.fy_list.length > 0)
    ? history.fy_list
    : [currentFYLabel(), currentFYLabel() - 1, currentFYLabel() - 2, currentFYLabel() - 3, currentFYLabel() - 4];
  const monthOptions = (history?.month_list && history.month_list.length > 0)
    ? history.month_list
    : monthsInFY(currentFYLabel());

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 2, borderLeft: '4px solid #0D2C54' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
          <TrendingUpIcon sx={{ fontSize: 32, color: '#0D2C54' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="bold">New Sales by Salesperson &mdash; Work in Progress</Typography>
            <Typography variant="body2" color="text.secondary">
              Project-level rollup of new sales in the window — # of projects (not jobs),
              value at time of sale vs value at time of close. One project = multiple Sage jobs
              (AV/IT, Lighting, Shades, etc.) grouped by AR customer code.
            </Typography>
          </Box>
          <Chip label="Owner-only" size="small" color="primary" variant="outlined" />
        </Box>
      </Paper>

      {/* Top-level view tabs: Window detail vs Historical by salesperson */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Tabs
          value={viewMode}
          onChange={(_, v) => setViewMode(v)}
          sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab value="window" label="Window detail" />
          <Tab value="history" label="History by salesperson" />
        </Tabs>
      </Paper>

      {viewMode === 'window' && (
      <>
      {/* Period controls (Window view) */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <ToggleButtonGroup
            value={periodMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setPeriodMode(v)}
          >
            <ToggleButton value="fy">Fiscal Year</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>

          {periodMode === 'fy' && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="fy-select-label">Fiscal Year</InputLabel>
              <Select
                labelId="fy-select-label"
                label="Fiscal Year"
                value={fyOptions.includes(Number(periodFY)) ? Number(periodFY) : (fyOptions[0] || currentFYLabel())}
                onChange={(e) => setPeriodFY(e.target.value)}
              >
                {fyOptions.map(fy => (
                  <MenuItem key={fy} value={fy}>
                    FY{String(fy).padStart(2, '0')} {fy === currentFYLabel() ? '(to date)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {periodMode === 'month' && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                label="Month"
                value={monthOptions.includes(periodMonth) ? periodMonth : (monthOptions[0] || currentMonth())}
                onChange={(e) => setPeriodMonth(e.target.value)}
              >
                {monthOptions.map(ym => (
                  <MenuItem key={ym} value={ym}>{monthLabel(ym)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {periodMode === 'custom' && (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
            </>
          )}

          <Chip
            icon={<CalendarTodayIcon />}
            label={periodLabel}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />

          <Box sx={{ flex: 1 }} />
          <ToggleButtonGroup
            value={scope}
            exclusive
            size="small"
            onChange={(_, v) => v && setScope(v)}
          >
            <ToggleButton value="all">All Projects</ToggleButton>
            <ToggleButton value="closed">Closed Only</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Run'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data && (
        <>
          {/* Totals strip */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title="Projects"
                value={scope === 'closed' ? totals.closed_project_count : totals.project_count}
                subtitle={scope === 'closed' ? 'closed in window' : `${totals.job_count} jobs total`}
                color="#0D2C54"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title="Salespeople"
                value={totals.salesperson_count}
                subtitle="active in window"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title="Sold $"
                value={fmtMoney(scope === 'closed' ? totals.closed_value_at_sale : totals.value_at_sale)}
                subtitle="value at time of sale"
                color="primary.main"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title={scope === 'closed' ? 'Closed $' : 'Current/Close $'}
                value={fmtMoney(scope === 'closed' ? totals.closed_value_at_close : totals.value_at_close)}
                subtitle={scope === 'closed' ? 'value at time of close' : 'revised contract today'}
                color="success.main"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title="Δ $"
                value={fmtMoney(scope === 'closed' ? totals.closed_delta : totals.delta)}
                subtitle="close − sale"
                color={deltaColor(scope === 'closed' ? totals.closed_delta : totals.delta)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <SummaryCard
                title="Δ %"
                value={fmtPct(scope === 'closed' ? totals.closed_delta_pct : totals.delta_pct)}
                subtitle="growth from sale → close"
                color={deltaColor(scope === 'closed' ? totals.closed_delta : totals.delta)}
              />
            </Grid>
          </Grid>

          {/* By Salesperson */}
          <Paper elevation={1} sx={{ mb: 2 }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0D2C54' }}>By Salesperson</Typography>
              <Typography variant="caption" color="text.secondary">
                Click a salesperson to filter the project list below
              </Typography>
            </Box>
            {salespeople.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No new sales in this window.</Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ px: 1.5, py: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                  <Chip
                    label={`All (${data.projects.length})`}
                    size="small"
                    color={salesmanFilter === 'all' ? 'primary' : 'default'}
                    variant={salesmanFilter === 'all' ? 'filled' : 'outlined'}
                    onClick={() => setSalesmanFilter('all')}
                    clickable
                  />
                  {salespeople.map(s => (
                    <Chip
                      key={s.salesman}
                      label={`${s.salesman} (${s.project_count})`}
                      size="small"
                      color={salesmanFilter === s.salesman ? 'primary' : 'default'}
                      variant={salesmanFilter === s.salesman ? 'filled' : 'outlined'}
                      onClick={() => setSalesmanFilter(s.salesman)}
                      clickable
                    />
                  ))}
                </Box>
                <SalespeopleTable rows={salespeople} scope={scope} />
              </Box>
            )}
          </Paper>

          {/* Project detail */}
          <Paper elevation={1}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0D2C54' }}>
                Projects {salesmanFilter !== 'all' ? `— ${salesmanFilter}` : ''}
                {' '}<Typography component="span" variant="caption" color="text.secondary">({projects.filter(p => scope !== 'closed' || p.status === 'Closed').length})</Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Expand a row to see constituent Sage jobs
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' } }}>
                    <TableCell sx={{ width: 36 }} />
                    <TableCell>Project</TableCell>
                    <TableCell>Salesperson</TableCell>
                    <TableCell align="center">Jobs</TableCell>
                    <TableCell align="right">Value at Sale</TableCell>
                    <TableCell align="right">Value at Close</TableCell>
                    <TableCell align="right">Δ</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projects
                    .filter(p => scope !== 'closed' || p.status === 'Closed')
                    .map(p => <ProjectRow key={p.project_key} p={p} />)
                  }
                  {projects.filter(p => scope !== 'closed' || p.status === 'Closed').length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          No projects match the current filter.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Methodology footer */}
          <Paper elevation={0} sx={{ mt: 2, p: 1.5, bgcolor: '#f9f9f9', borderLeft: '3px solid #bdbdbd' }}>
            <Typography variant="caption" color="text.secondary" component="div">
              <strong>Method:</strong> "New sale" = Sage JCT 'Scheduled value' transaction in the window
              (same rule as Pat's New Business by Salesperson). Jobs are grouped into projects by AR
              customer code (with a fallback to local project linkage). "Value at sale" = SUM of
              Original_Contract_Amount across the project's jobs. "Value at close" = SUM of
              Revised_Contract_Amount — for closed projects this is the final closed contract; for
              in-progress projects it's the current revised amount.
            </Typography>
          </Paper>
        </>
      )}
      </>
      )}

      {viewMode === 'history' && (
        <HistoryPanel
          history={history}
          loading={historyLoading}
          error={historyError}
          onRetry={loadHistory}
          onJumpToFY={(fy) => { setViewMode('window'); setPeriodMode('fy'); setPeriodFY(fy); }}
          onJumpToMonth={(ym) => { setViewMode('window'); setPeriodMode('month'); setPeriodMonth(ym); }}
          fyLookup={fyLookup}
        />
      )}
    </Box>
  );
};

// ─── History panel ───────────────────────────────────────────────────────────────
function HistoryPanel({ history, loading, error, onRetry, onJumpToFY, onJumpToMonth, fyLookup }) {
  const [orderBy, setOrderBy] = useState('value_at_sale');
  const [order, setOrder] = useState('desc');
  const [scope, setScope] = useState('all'); // 'all' | 'closed'
  const [selectedSalesman, setSelectedSalesman] = useState(null);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        severity="error"
        action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}
      >
        {error}
      </Alert>
    );
  }
  if (!history) return null;

  const lifetime = history.salespeople || [];
  const fys = history.fy_list || [];
  const byFy = history.by_fy || [];
  const monthsForSelected = selectedSalesman
    ? (history.by_salesman_month || []).filter(r => r.salesman === selectedSalesman)
    : [];

  const handleSort = (id) => {
    if (orderBy === id) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(id); setOrder(id === 'salesman' ? 'asc' : 'desc'); }
  };

  const sortedLifetime = [...lifetime].sort((a, b) => {
    const av = a[orderBy], bv = b[orderBy];
    if (typeof av === 'string' || typeof bv === 'string') {
      return order === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    }
    const an = Number(av || 0), bn = Number(bv || 0);
    return order === 'asc' ? an - bn : bn - an;
  });

  const headers = scope === 'closed'
    ? [
        { id: 'salesman', label: 'Salesperson', align: 'left' },
        { id: 'closed_job_count', label: 'Closed Jobs', align: 'center' },
        { id: 'closed_value_at_sale', label: 'Sold $', align: 'right' },
        { id: 'closed_value_at_close', label: 'Closed $', align: 'right' },
        { id: 'closed_delta', label: 'Δ $', align: 'right' },
        { id: 'closed_delta_pct', label: 'Δ %', align: 'right' },
      ]
    : [
        { id: 'salesman', label: 'Salesperson', align: 'left' },
        { id: 'first_sale_date', label: 'First Sale', align: 'left' },
        { id: 'last_sale_date', label: 'Last Sale', align: 'left' },
        { id: 'project_count', label: 'Projects', align: 'center' },
        { id: 'job_count', label: 'Jobs', align: 'center' },
        { id: 'value_at_sale', label: 'Sold $', align: 'right' },
        { id: 'value_at_close', label: 'Current/Close $', align: 'right' },
        { id: 'delta', label: 'Δ $', align: 'right' },
        { id: 'delta_pct', label: 'Δ %', align: 'right' },
      ];

  return (
    <>
      {/* Coverage strip */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Coverage:</strong> {history.coverage?.earliest_sale || '—'} → {history.coverage?.latest_sale || '—'} ·
          {' '}{history.coverage?.total_sale_events?.toLocaleString()} sale events ·
          {' '}{history.coverage?.total_jobs?.toLocaleString()} jobs ·
          {' '}{lifetime.length} salespeople
        </Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          value={scope}
          exclusive
          size="small"
          onChange={(_, v) => v && setScope(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="closed">Closed Only</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* FY totals strip (across all salespeople). Click an FY to jump back
          to the Window detail filtered to that fiscal year. */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0D2C54' }}>By Fiscal Year</Typography>
          <Typography variant="caption" color="text.secondary">Click an FY to drill into Window detail</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' } }}>
                <TableCell>FY</TableCell>
                <TableCell align="center">Projects</TableCell>
                <TableCell align="center">Jobs</TableCell>
                <TableCell align="right">Sold $</TableCell>
                <TableCell align="right">Current/Close $</TableCell>
                <TableCell align="right">Δ $</TableCell>
                <TableCell align="right">Δ %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byFy.map(r => (
                <TableRow
                  key={r.fy}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onJumpToFY(r.fy)}
                >
                  <TableCell><Typography variant="body2" fontWeight={600}>FY{String(r.fy).padStart(2, '0')}</Typography></TableCell>
                  <TableCell align="center">{r.project_count}</TableCell>
                  <TableCell align="center">{r.job_count}</TableCell>
                  <TableCell align="right">{fmtMoney(r.value_at_sale)}</TableCell>
                  <TableCell align="right">{fmtMoney(r.value_at_close)}</TableCell>
                  <TableCell align="right" sx={{ color: deltaColor(r.delta), fontWeight: 600 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <DeltaIcon value={r.delta} />{fmtMoney(r.delta)}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: deltaColor(r.delta), fontWeight: 600 }}>{fmtPct(r.delta_pct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Lifetime per-salesperson rollup */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0D2C54' }}>
            Lifetime totals by salesperson
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Click a salesperson to see their FY breakdown below
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' } }}>
                {headers.map(h => (
                  <TableCell key={h.id} align={h.align} sortDirection={orderBy === h.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === h.id}
                      direction={orderBy === h.id ? order : 'asc'}
                      onClick={() => handleSort(h.id)}
                    >
                      {h.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLifetime.map(s => {
                const isClosedScope = scope === 'closed';
                const sold  = isClosedScope ? s.closed_value_at_sale  : s.value_at_sale;
                const close = isClosedScope ? s.closed_value_at_close : s.value_at_close;
                const delta = isClosedScope ? s.closed_delta          : s.delta;
                const dpct  = isClosedScope ? s.closed_delta_pct      : s.delta_pct;
                return (
                  <TableRow
                    key={s.salesman}
                    hover
                    sx={{ cursor: 'pointer', bgcolor: selectedSalesman === s.salesman ? 'action.selected' : undefined }}
                    onClick={() => setSelectedSalesman(s.salesman === selectedSalesman ? null : s.salesman)}
                  >
                    <TableCell><Typography variant="body2" fontWeight={600}>{s.salesman}</Typography></TableCell>
                    {!isClosedScope && <TableCell><Typography variant="caption">{s.first_sale_date || '—'}</Typography></TableCell>}
                    {!isClosedScope && <TableCell><Typography variant="caption">{s.last_sale_date || '—'}</Typography></TableCell>}
                    <TableCell align="center">{isClosedScope ? s.closed_job_count : s.project_count}</TableCell>
                    {!isClosedScope && <TableCell align="center">{s.job_count}</TableCell>}
                    <TableCell align="right">{fmtMoney(sold)}</TableCell>
                    <TableCell align="right">{fmtMoney(close)}</TableCell>
                    <TableCell align="right" sx={{ color: deltaColor(delta), fontWeight: 600 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <DeltaIcon value={delta} />{fmtMoney(delta)}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ color: deltaColor(delta), fontWeight: 600 }}>{fmtPct(dpct)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FY × salesperson matrix — visible when a salesperson is selected */}
      {selectedSalesman && fyLookup && (
        <Paper elevation={1} sx={{ mb: 2 }}>
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#0D2C54' }}>
              {selectedSalesman} — by Fiscal Year
            </Typography>
            <Button size="small" onClick={() => setSelectedSalesman(null)}>Clear</Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5' } }}>
                  <TableCell>FY</TableCell>
                  <TableCell align="center">Projects</TableCell>
                  <TableCell align="center">Jobs</TableCell>
                  <TableCell align="right">Sold $</TableCell>
                  <TableCell align="right">Current/Close $</TableCell>
                  <TableCell align="right">Δ $</TableCell>
                  <TableCell align="right">Δ %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fys.map(fy => {
                  const r = fyLookup.get(`${selectedSalesman}__${fy}`);
                  if (!r) {
                    return (
                      <TableRow key={fy} hover sx={{ cursor: 'pointer' }} onClick={() => onJumpToFY(fy)}>
                        <TableCell><Typography variant="body2" fontWeight={600}>FY{String(fy).padStart(2, '0')}</Typography></TableCell>
                        <TableCell colSpan={6} sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.72rem' }}>no sales</TableCell>
                      </TableRow>
                    );
                  }
                  return (
                    <TableRow key={fy} hover sx={{ cursor: 'pointer' }} onClick={() => onJumpToFY(fy)}>
                      <TableCell><Typography variant="body2" fontWeight={600}>FY{String(fy).padStart(2, '0')}</Typography></TableCell>
                      <TableCell align="center">{r.project_count}</TableCell>
                      <TableCell align="center">{r.job_count}</TableCell>
                      <TableCell align="right">{fmtMoney(r.value_at_sale)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.value_at_close)}</TableCell>
                      <TableCell align="right" sx={{ color: deltaColor(r.delta), fontWeight: 600 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <DeltaIcon value={r.delta} />{fmtMoney(r.delta)}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ color: deltaColor(r.delta), fontWeight: 600 }}>{fmtPct(r.delta_pct)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {monthsForSelected.length > 0 && (
            <>
              <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
                <Typography variant="caption" fontWeight="bold" sx={{ color: '#0D2C54' }}>
                  Monthly detail ({monthsForSelected.length} months) — click a row to drill in
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.7rem', bgcolor: '#f5f5f5' } }}>
                      <TableCell>Month</TableCell>
                      <TableCell>FY</TableCell>
                      <TableCell align="center">Jobs</TableCell>
                      <TableCell align="right">Sold $</TableCell>
                      <TableCell align="right">Current/Close $</TableCell>
                      <TableCell align="right">Δ %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthsForSelected.map(r => (
                      <TableRow key={r.month} hover sx={{ cursor: 'pointer' }} onClick={() => onJumpToMonth(r.month)}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{monthLabel(r.month)}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">FY{String(r.fy).padStart(2, '0')}</Typography></TableCell>
                        <TableCell align="center">{r.job_count}</TableCell>
                        <TableCell align="right">{fmtMoney(r.value_at_sale)}</TableCell>
                        <TableCell align="right">{fmtMoney(r.value_at_close)}</TableCell>
                        <TableCell align="right" sx={{ color: deltaColor(r.delta), fontWeight: 600 }}>{fmtPct(r.delta_pct)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      <Paper elevation={0} sx={{ mt: 2, p: 1.5, bgcolor: '#f9f9f9', borderLeft: '3px solid #bdbdbd' }}>
        <Typography variant="caption" color="text.secondary" component="div">
          <strong>History method:</strong> Lifetime totals roll up every Sage JCT 'Scheduled value' transaction
          on file (back to {history.coverage?.earliest_sale || '—'}). Each job is attributed to the salesperson
          on the JCM master record and assigned to the ACS fiscal year that contains the earliest sale-event
          date. Click any FY row to jump to the Window detail filtered to that fiscal year.
        </Typography>
      </Paper>
    </>
  );
}

export default NewSalesBySalesperson;
